/**
 * LinkAccountModal
 *
 * Flow realista de vinculación de una cuenta de WhatsApp por QR.
 *
 * 5 estados:
 *  1. "preflight"  → checklist: WA abierto, internet, cel cargado
 *  2. "form"       → displayName + scope (global/local) + local
 *  3. "waiting"    → QR + countdown 30s. Auto-scan a 4s
 *  4. "syncing"    → multi-step: pairing handshake → perfil → contactos → webhooks
 *  5. "success"    → CTA "Completar configuración →" redirect a /setup
 *
 * Cada substep del syncing dura 800-1400ms con label propio. Replica el flow
 * real de Baileys: pairing Noise protocol → fetch profile → fetch contacts →
 * register webhooks → ready.
 */

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Loader2,
    Smartphone,
    CheckCircle2,
    X,
    ChevronRight,
    Wifi,
    Globe,
    MapPin,
    Battery,
    KeyRound,
    UserCircle2,
    Users as UsersIcon,
    Webhook,
    Settings,
} from "lucide-react";
import QRCodeMock from "./QRCodeMock";
import { Button } from "../../../shared/components/ui/Button";
import { whatsappAccountsApi } from "../api";
import type { DeenexMultiLocal } from "../../../hooks/useIntegracion";
import type { WhatsAppAccountScope } from "../types";

interface LinkAccountModalProps {
    open: boolean;
    onClose: () => void;
    onLinked: () => void;
    /** Locales disponibles (vienen de useIntegracion.locales) */
    locales: DeenexMultiLocal[];
}

type Step = "preflight" | "form" | "waiting" | "syncing" | "success";

const AUTO_SCAN_AFTER_MS = 4000;
const QR_EXPIRES_SECONDS = 30;

/** Sub-steps del syncing — cada uno tarda este tiempo en ms */
const SYNC_STEPS = [
    { key: "pairing", label: "Conectando con WhatsApp", icon: KeyRound, ms: 900 },
    { key: "profile", label: "Trayendo tu perfil", icon: UserCircle2, ms: 1100 },
    { key: "contacts", label: "Sincronizando tus contactos", icon: UsersIcon, ms: 1500 },
    { key: "webhooks", label: "Activando las notificaciones", icon: Webhook, ms: 800 },
] as const;

export default function LinkAccountModal({
    open,
    onClose,
    onLinked,
    locales,
}: LinkAccountModalProps) {
    const navigate = useNavigate();
    const [step, setStep] = useState<Step>("preflight");
    const [displayName, setDisplayName] = useState("");
    const [scope, setScope] = useState<WhatsAppAccountScope>("local");
    const [localId, setLocalId] = useState<string>("");

    const [pairingId, setPairingId] = useState<string | null>(null);
    const [accountId, setAccountId] = useState<string | null>(null);
    const [qrPayload, setQrPayload] = useState<string>("");
    const [countdown, setCountdown] = useState(QR_EXPIRES_SECONDS);

    const [phoneNumber, setPhoneNumber] = useState<string>("");
    const [syncStepIndex, setSyncStepIndex] = useState(0);

    const [submitError, setSubmitError] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);

    /* Reset al abrir */
    useEffect(() => {
        if (open) {
            setStep("preflight");
            setDisplayName("");
            setScope("local");
            setLocalId("");
            setPairingId(null);
            setAccountId(null);
            setQrPayload("");
            setCountdown(QR_EXPIRES_SECONDS);
            setPhoneNumber("");
            setSyncStepIndex(0);
            setSubmitError(null);
            setSubmitting(false);
        }
    }, [open]);

    /* Countdown durante waiting */
    useEffect(() => {
        if (step !== "waiting" || countdown <= 0) return;
        const t = setTimeout(() => setCountdown((c) => c - 1), 1000);
        return () => clearTimeout(t);
    }, [step, countdown]);

    /* Auto-scan después de N segundos */
    useEffect(() => {
        if (step !== "waiting" || !pairingId) return;
        const t = setTimeout(() => handleSimulateScan(), AUTO_SCAN_AFTER_MS);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [step, pairingId]);

    /* Sync steps progression */
    useEffect(() => {
        if (step !== "syncing") return;
        if (syncStepIndex >= SYNC_STEPS.length) return;
        const ms = SYNC_STEPS[syncStepIndex].ms;
        const t = setTimeout(() => setSyncStepIndex((i) => i + 1), ms);
        return () => clearTimeout(t);
    }, [step, syncStepIndex]);

    /* Cuando termina el sync → confirm en backend + success */
    useEffect(() => {
        if (step !== "syncing") return;
        if (syncStepIndex < SYNC_STEPS.length) return;
        (async () => {
            if (!pairingId) return;
            const res = await whatsappAccountsApi.confirmLink(pairingId);
            if (!res.success) {
                setSubmitError(res.error || "Error confirmando el scan");
                return;
            }
            setPhoneNumber(res.data.phoneNumber);
            setAccountId(res.data.id);
            setStep("success");
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [step, syncStepIndex]);

    const handleStartLink = async () => {
        if (!displayName.trim()) {
            setSubmitError("Ponele un nombre a esta cuenta");
            return;
        }
        if (scope === "local" && !localId) {
            setSubmitError("Elegí a qué local pertenece esta cuenta");
            return;
        }
        setSubmitting(true);
        setSubmitError(null);
        const res = await whatsappAccountsApi.startLink({
            brandId: "brand-demo",
            displayName: displayName.trim(),
            scope,
            localId: scope === "local" ? localId : undefined,
        });
        setSubmitting(false);
        if (!res.success) {
            setSubmitError(res.error || "No se pudo iniciar el pairing");
            return;
        }
        setPairingId(res.data.pairingId);
        setQrPayload(res.data.qrPayload);
        setCountdown(res.data.expiresInSeconds || QR_EXPIRES_SECONDS);
        setStep("waiting");
    };

    const handleSimulateScan = () => {
        // No confirmamos todavía — primero pasamos por el syncing animado.
        // El confirm real se dispara cuando syncStepIndex llega al final.
        setSyncStepIndex(0);
        setStep("syncing");
    };

    const handleGoToSetup = () => {
        onLinked();
        onClose();
        if (accountId) navigate(`/whatsapp/accounts/${accountId}/setup`);
    };

    if (!open) return null;

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4 animate-fade-in"
            onClick={onClose}
        >
            <div
                role="dialog"
                aria-modal="true"
                aria-label="Vincular cuenta de WhatsApp"
                className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-center justify-between p-5 border-b border-slate-100">
                    <div className="flex items-center gap-2.5">
                        <div className="p-1.5 rounded-lg bg-green-100 text-green-700">
                            <Smartphone size={16} />
                        </div>
                        <div>
                            <h2 className="text-sm font-bold text-slate-900">
                                Vincular nuevo número
                            </h2>
                            <p className="text-[11px] text-slate-500">
                                {step === "preflight" && "Antes de empezar"}
                                {step === "form" && "Configurá la cuenta"}
                                {step === "waiting" && "Escaneá el QR desde tu WhatsApp"}
                                {step === "syncing" && "Sincronizando con WhatsApp"}
                                {step === "success" && "¡Listo!"}
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                    >
                        <X size={16} />
                    </button>
                </div>

                {/* Step progress dots */}
                <StepDots current={step} />

                {/* ─── Step 1: preflight ─── */}
                {step === "preflight" && (
                    <div className="p-5 space-y-4">
                        <p className="text-xs text-slate-600 leading-relaxed">
                            Vinculamos un número de WhatsApp existente como si fueras
                            a WhatsApp Web. Antes de continuar, verificá:
                        </p>
                        <ul className="space-y-2.5">
                            <PrecheckItem
                                icon={<Smartphone size={14} />}
                                label="Tenés WhatsApp instalado en tu celular"
                            />
                            <PrecheckItem
                                icon={<Wifi size={14} />}
                                label="El celular tiene internet estable"
                            />
                            <PrecheckItem
                                icon={<Battery size={14} />}
                                label="La batería está por encima del 20%"
                            />
                            <PrecheckItem
                                icon={<Settings size={14} />}
                                label="WhatsApp → Configuración → Dispositivos vinculados está accesible"
                            />
                        </ul>
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-[11px] text-amber-800">
                            <p className="font-semibold mb-1">⚠️ Importante</p>
                            <p className="leading-relaxed">
                                No cierres WhatsApp en el celular después de vincularlo
                                — si lo cerrás por mucho tiempo, la sesión se desconecta.
                            </p>
                        </div>
                        <Button
                            variant="primary"
                            onClick={() => setStep("form")}
                            className="w-full"
                        >
                            Estoy listo
                            <ChevronRight size={14} />
                        </Button>
                    </div>
                )}

                {/* ─── Step 2: form ─── */}
                {step === "form" && (
                    <div className="p-5 space-y-4">
                        <div>
                            <label className="text-xs font-semibold text-slate-700 mb-1.5 block">
                                Nombre de la cuenta
                            </label>
                            <input
                                type="text"
                                value={displayName}
                                onChange={(e) => setDisplayName(e.target.value)}
                                placeholder="La Bistecca San Telmo"
                                className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-900 focus:outline-none focus:border-green-300 focus:ring-1 focus:ring-green-100"
                                autoFocus
                            />
                            <p className="text-[10px] text-slate-400 mt-1">
                                Para distinguirla en la lista — no es visible para los clientes.
                            </p>
                        </div>

                        <div>
                            <label className="text-xs font-semibold text-slate-700 mb-1.5 block">
                                Tipo de cuenta
                            </label>
                            <div className="grid grid-cols-2 gap-2">
                                <ScopeOption
                                    selected={scope === "global"}
                                    onClick={() => setScope("global")}
                                    icon={<Globe size={16} />}
                                    label="Global"
                                    description="La usan todos los locales"
                                />
                                <ScopeOption
                                    selected={scope === "local"}
                                    onClick={() => setScope("local")}
                                    icon={<MapPin size={16} />}
                                    label="De un local"
                                    description="Solo el local que elijas"
                                />
                            </div>
                        </div>

                        {scope === "local" && locales.length > 0 && (
                            <div>
                                <label className="text-xs font-semibold text-slate-700 mb-1.5 block">
                                    Local asignado
                                </label>
                                <select
                                    value={localId}
                                    onChange={(e) => setLocalId(e.target.value)}
                                    className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-900 focus:outline-none focus:border-green-300 cursor-pointer"
                                >
                                    <option value="">— Elegí un local —</option>
                                    {locales.map((l) => (
                                        <option key={l._id} value={l._id}>
                                            {l.nameLocal}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}

                        {submitError && (
                            <div className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg p-2.5">
                                {submitError}
                            </div>
                        )}

                        <div className="flex gap-2 pt-1">
                            <Button
                                variant="secondary"
                                onClick={() => setStep("preflight")}
                                className="flex-1"
                            >
                                Atrás
                            </Button>
                            <Button
                                variant="primary"
                                onClick={handleStartLink}
                                disabled={submitting || !displayName.trim()}
                                className="flex-1"
                            >
                                {submitting ? (
                                    <Loader2 size={14} className="animate-spin" />
                                ) : (
                                    <>
                                        Generar QR
                                        <ChevronRight size={14} />
                                    </>
                                )}
                            </Button>
                        </div>
                    </div>
                )}

                {/* ─── Step 3: waiting (QR) ─── */}
                {step === "waiting" && (
                    <div className="p-5 flex flex-col items-center text-center space-y-4">
                        <QRCodeMock payload={qrPayload} size={220} />

                        <div className="space-y-1">
                            <p className="text-sm font-semibold text-slate-900">
                                Escaneá desde WhatsApp
                            </p>
                            <p className="text-[11px] text-slate-500 max-w-[280px] leading-relaxed">
                                Abrí WhatsApp en tu celular → ⚙️ Ajustes → Dispositivos vinculados → Vincular un dispositivo.
                            </p>
                        </div>

                        <div className="flex items-center gap-1.5 text-xs text-slate-500">
                            <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" />
                            Esperando scan… expira en{" "}
                            <span className="font-mono font-bold text-slate-700">
                                {countdown}s
                            </span>
                        </div>

                        <button
                            onClick={handleSimulateScan}
                            className="text-[11px] text-violet-600 hover:text-violet-800 underline underline-offset-2"
                        >
                            🎬 Simular scan ahora (modo demo)
                        </button>
                    </div>
                )}

                {/* ─── Step 4: syncing ─── */}
                {step === "syncing" && (
                    <div className="p-5 space-y-3">
                        <p className="text-xs text-slate-600 mb-2">
                            Sincronizando con WhatsApp. Esto puede tardar unos segundos…
                        </p>
                        <ul className="space-y-2">
                            {SYNC_STEPS.map((s, i) => {
                                const Icon = s.icon;
                                const status =
                                    i < syncStepIndex
                                        ? "done"
                                        : i === syncStepIndex
                                          ? "running"
                                          : "pending";
                                return (
                                    <li
                                        key={s.key}
                                        className={`flex items-center gap-3 p-2.5 rounded-lg border ${
                                            status === "running"
                                                ? "border-violet-200 bg-violet-50/50"
                                                : status === "done"
                                                  ? "border-emerald-200 bg-emerald-50/50"
                                                  : "border-slate-100 bg-slate-50/50"
                                        }`}
                                    >
                                        <div
                                            className={`p-1.5 rounded-md ${
                                                status === "done"
                                                    ? "bg-emerald-100 text-emerald-700"
                                                    : status === "running"
                                                      ? "bg-violet-100 text-violet-700"
                                                      : "bg-slate-100 text-slate-400"
                                            }`}
                                        >
                                            {status === "done" ? (
                                                <CheckCircle2 size={14} />
                                            ) : status === "running" ? (
                                                <Loader2 size={14} className="animate-spin" />
                                            ) : (
                                                <Icon size={14} />
                                            )}
                                        </div>
                                        <span
                                            className={`text-xs flex-1 ${
                                                status === "done"
                                                    ? "text-slate-700"
                                                    : status === "running"
                                                      ? "text-slate-900 font-medium"
                                                      : "text-slate-400"
                                            }`}
                                        >
                                            {s.label}
                                        </span>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                )}

                {/* ─── Step 5: success ─── */}
                {step === "success" && (
                    <div className="p-6 flex flex-col items-center text-center space-y-4">
                        <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center">
                            <CheckCircle2 size={32} className="text-green-600" />
                        </div>
                        <div>
                            <p className="text-base font-bold text-slate-900">
                                ¡Cuenta vinculada!
                            </p>
                            <p className="text-xs text-slate-600 mt-1">
                                {displayName} ·{" "}
                                <span className="font-mono">{phoneNumber}</span>
                            </p>
                        </div>

                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-[11px] text-amber-900 text-left w-full">
                            <p className="font-semibold mb-1">
                                ⚙️ Falta la configuración
                            </p>
                            <p className="leading-relaxed">
                                Antes de poder enviar campañas masivas, completá:
                                perfil de negocio, mensaje de prueba, auto-respuestas
                                y plan de warm-up. Toma 3-5 minutos.
                            </p>
                        </div>

                        <div className="flex gap-2 w-full">
                            <Button
                                variant="secondary"
                                onClick={() => {
                                    onLinked();
                                    onClose();
                                }}
                                className="flex-1"
                            >
                                Configurar más tarde
                            </Button>
                            <Button
                                variant="primary"
                                onClick={handleGoToSetup}
                                className="flex-1"
                            >
                                Completar setup
                                <ChevronRight size={14} />
                            </Button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

/* ─── Step dots indicator ─── */
function StepDots({ current }: { current: Step }) {
    const steps: Step[] = ["preflight", "form", "waiting", "syncing", "success"];
    const idx = steps.indexOf(current);
    return (
        <div className="flex items-center justify-center gap-1.5 py-2.5 border-b border-slate-100">
            {steps.map((s, i) => (
                <div
                    key={s}
                    className={`h-1 rounded-full transition-all duration-300 ${
                        i === idx
                            ? "w-6 bg-green-500"
                            : i < idx
                              ? "w-2 bg-green-300"
                              : "w-2 bg-slate-200"
                    }`}
                />
            ))}
        </div>
    );
}

/* ─── Preflight checklist item ─── */
function PrecheckItem({
    icon,
    label,
}: {
    icon: React.ReactNode;
    label: string;
}) {
    return (
        <li className="flex items-center gap-3 p-2.5 rounded-lg bg-slate-50 border border-slate-100">
            <span className="p-1 rounded bg-white text-emerald-600 border border-slate-200">
                {icon}
            </span>
            <span className="text-xs text-slate-700 flex-1">{label}</span>
            <CheckCircle2 size={14} className="text-emerald-500" />
        </li>
    );
}

/* ─── Scope option visual ─── */
function ScopeOption({
    selected,
    onClick,
    icon,
    label,
    description,
}: {
    selected: boolean;
    onClick: () => void;
    icon: React.ReactNode;
    label: string;
    description: string;
}) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={`text-left p-3 rounded-lg border-2 transition-all ${
                selected
                    ? "border-green-500 bg-green-50/50 ring-1 ring-green-200"
                    : "border-slate-200 bg-white hover:bg-slate-50"
            }`}
        >
            <div className="flex items-center gap-2 mb-1">
                <span className={selected ? "text-green-600" : "text-slate-400"}>
                    {icon}
                </span>
                <span className="text-sm font-semibold text-slate-900">
                    {label}
                </span>
            </div>
            <p className="text-[10px] text-slate-500 leading-snug">
                {description}
            </p>
        </button>
    );
}
